<script lang="ts" setup>
import Konva from "konva";
import type { CSSProperties } from "vue";
import { nextTick } from "vue";
import { vOnClickOutside } from "@vueuse/components";
import type { Shape } from "@/telestrations/types/canvas";

const props = defineProps<{
  shape: Shape;
}>();

const { t } = useI18n();

const textareaShown = ref(false);
const textareaRef = ref<HTMLTextAreaElement | null>(null);
const textareaStyles = ref<Partial<CSSProperties> | null>(null);
const shapesStore = useShapesStore();
/**
 * This is a workaround for missclicking on the textarea when it's shown.
 * TODO: Rewrite to a better solution.
 */
const {
  start: startTextareaAvailabilityCountdown,
  remaining: remainingTextareaAvailabilityCountdown,
  reset: resetTextareaAvailabilityCountdown,
} = useCountdown(1);

const textNodeRef = ref<Konva.Transformer | null>(null);

const backgroundColor = computed(() => props.shape.background || "transparent");

const textConfig = computed(() => {
  const baseConfig = { ...props.shape };
  // Inject custom sceneFunc if background is not transparent
  if (backgroundColor.value && backgroundColor.value !== "transparent") {
    baseConfig.sceneFunc = function (context, shape) {
      // Draw background
      context.fillStyle = backgroundColor.value;
      context.fillRect(0, 0, shape.width(), shape.height());
      // Draw text (call original Konva.Text sceneFunc)
      Konva.Text.prototype._sceneFunc.call(shape, context);
    };
  }
  else {
    // Remove sceneFunc if background is transparent
    delete baseConfig.sceneFunc;
  }
  return baseConfig;
});

const textContent = ref("");
const spanRef = ref<HTMLSpanElement | null>(null);

function updateTextareaWidth() {
  if (!textareaRef.value || !spanRef.value) return;
  const measureText = textContent.value || textareaRef.value.placeholder;
  spanRef.value.textContent = measureText;

  // Set textarea width to span width + padding
  const spanWidth = spanRef.value.offsetWidth;
  textareaRef.value.style.width = spanWidth + 2 + "px";
  spanRef.value.textContent = textContent.value;
}

function handleTextareaInput(e: Event) {
  resetTextareaAvailabilityCountdown(0);
  const value = (e.target as HTMLTextAreaElement).value;
  textContent.value = value || "";
  nextTick(updateTextareaWidth);
}

function handleDragEnd(e: Event) {
  shapesStore.onShapeDragEnd(e, props.shape);
}

function handleShapeClick() {
  shapesStore.selectShape(props.shape);
}
function handleTransformEnd(e: Event) {
  shapesStore.onShapeTransformEnd(e);
}

function openTextareaForEditing(textNode: Konva.Text) {
  const stage = textNode.getStage();
  if (!stage) {
    console.warn("No stage found");
    return;
  }

  // Hide Konva.Text node while editing
  textNode.visible(false);
  textNode.getLayer()?.draw();

  // first we need to find position for textarea
  // how to find it?

  // at first lets find position of text node relative to the stage:
  const textPosition = textNode.getAbsolutePosition();

  // Combine stage scale with text node scale
  const combinedScaleY = stage.scaleY() * textNode.scaleY();

  // Calculate scaled dimensions
  const scaledHeight = textNode.height() * combinedScaleY;

  // so position of textarea will be the sum of positions above:
  const areaPosition = {
    x: textPosition.x,
    y: textPosition.y,
  };

  const fontSize = textNode.fontSize() * combinedScaleY;
  const baselineOffset = fontSize * 0.05;

  textareaStyles.value = {
    position: "absolute",
    zIndex: "100",
    top: (areaPosition.y - baselineOffset) + "px",
    left: areaPosition.x + "px",
    height: scaledHeight + "px",
    color: typeof props.shape.fill === "string" ? props.shape.fill : "black",
    fontSize: fontSize + "px",
    background: backgroundColor.value !== "transparent" ? backgroundColor.value : "none",
    border: "1px solid #00A3FF",
    boxSizing: "content-box",
  };

  textareaShown.value = true;
  shapesStore.selectShape(null);
  startTextareaAvailabilityCountdown();

  nextTick(() => {
    if (!textareaRef.value) return;
    textareaRef.value.focus();

    textareaRef.value.value = textNode.text();
    // Ensure textContent is up to date and width is recalculated
    textContent.value = textareaRef.value.value || "";
    nextTick(updateTextareaWidth);
  });
}

function handleShapeDoubleClick(e: Event) {
  const textNode = e.target as Konva.Text | null;
  if (!textNode) {
    console.warn("No node found");
    return;
  }

  openTextareaForEditing(textNode);
}

function updateTextNodeAndCloseTextarea() {
  if (remainingTextareaAvailabilityCountdown.value > 0) {
    return;
  }
  if (!textareaRef.value || !textareaShown.value) return;

  const textNode = shapesStore.getShapeNode(props.shape) as Konva.Text | null;
  if (!textNode) {
    console.warn("No node found");
    return;
  }
  if (!textareaRef.value) return;

  textNode.text(textareaRef.value.value);
  textareaShown.value = false;
  resetTextareaAvailabilityCountdown();

  // Show Konva.Text node after editing
  textNode.visible(true);
  textNode.getLayer()?.draw();

  shapesStore.isDrawing = false;
  shapesStore.drawingShape = null;

  shapesStore.selectShape(props.shape);
}

function handleTextareaBlur() {
  if (!textareaRef.value) return;
  const value = textareaRef.value?.value?.trim();
  if (value === "") {
    // Remove the shape if the text is empty
    shapesStore.removeShape(props.shape.id);
    textareaShown.value = false;
    resetTextareaAvailabilityCountdown();
  }
}

onMounted(() => {
  // If the shape is new, immediately enter edit mode
  if (props.shape.isNew) {
    requestAnimationFrame(() => {
      const textNode = textNodeRef.value?.getNode() as Konva.Text;
      if (!textNode) {
        console.warn("No node found");
        return;
      }

      openTextareaForEditing(textNode);
      shapesStore.updateShape(props.shape, { isNew: false });
    });
  }
});
</script>

<template>
  <v-text
    ref="textNodeRef"
    :config="textConfig"
    @dragstart="shapesStore.onShapeDragStart"
    @dragmove="shapesStore.onShapeDragMove"
    @dragend="handleDragEnd"
    @click="handleShapeClick"
    @transformend="handleTransformEnd"
    @dblclick="handleShapeDoubleClick"
    @dbltap="handleShapeDoubleClick"
  />
  <Teleport to="#pixellotPlayer">
    <textarea
      v-if="textareaShown"
      ref="textareaRef"
      v-on-click-outside="updateTextNodeAndCloseTextarea"
      :placeholder="t('telestrations.text.placeholder')"
      class="absolute z-20 resize-none overflow-hidden border-none bg-transparent outline-none"
      :style="textareaStyles"
      @keydown.enter.prevent="updateTextNodeAndCloseTextarea"
      @blur="handleTextareaBlur"
      @keydown.stop
      @keyup.stop
      @keypress.stop
      @input="handleTextareaInput"
    />
    <!-- Hidden span for measuring text width -->
    <span
      v-if="textareaShown"
      ref="spanRef"
      class="invisible absolute"
      :style="{
        fontSize: textareaStyles?.fontSize,
      }"
    >{{ textContent }}</span>
  </Teleport>
</template>
